import {
  Box,
  Container,
  Divider,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { PublicLandingHeader } from '../../components/layout/PublicLandingHeader'

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="sm">
      <Title order={3} fz="lg">
        {title}
      </Title>
      {children}
    </Stack>
  )
}

export function PrivacyPolicy() {
  const theme = useMantineTheme()
  const isDark = useComputedColorScheme('dark') === 'dark'
  const bg = isDark ? theme.colors.dark[7] : '#fff'
  const border = isDark ? theme.colors.dark[4] : 'var(--sat-border-soft)'

  return (
    <Box style={{ background: isDark ? theme.colors.dark[8] : 'var(--sat-bg)', minHeight: '100vh' }}>
      <PublicLandingHeader />
      <Container size="md" py={60}>
        <Box
          p={{ base: 'xl', sm: 48 }}
          style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16 }}
        >
          <Stack gap="lg">
            <Stack gap={4}>
              <Title order={1} fz={{ base: 28, sm: 36 }} c="var(--sat-text)">
                Privacy Policy
              </Title>
              <Text fz="sm" c="dimmed">
                FinderAcademy, Inc. &nbsp;·&nbsp; Effective Date: January 1, 2026 &nbsp;·&nbsp; Last Updated: April 1, 2026
              </Text>
            </Stack>

            <Divider />

            <Text c="var(--sat-text-muted)" lh={1.8}>
              FinderAcademy, Inc. ("FinderAcademy", "we", "our", or "us") operates the SAT Tutor platform
              available at finderacademy.org (the "Service"). This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our Service. Please read this
              policy carefully. If you disagree with its terms, please discontinue use of the Service.
            </Text>

            <LegalSection title="1. Information We Collect">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Account Information.</strong> When you register, we collect your name, email
                address, password (hashed), and role (student or tutor).
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Usage Data.</strong> We automatically collect information about how you interact
                with the Service, including pages visited, questions answered, practice session
                durations, vocabulary progress, and assignment activity.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Payment Information.</strong> Payments are processed by Stripe, Inc. We do not
                store your full card number. We retain a record of the transaction amount, date, and
                Stripe payment reference ID.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Communications.</strong> If you contact us via email or our support system, we
                retain the content of that communication to respond and improve our Service.
              </Text>
            </LegalSection>

            <LegalSection title="2. How We Use Your Information">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We use the information we collect to:
              </Text>
              <Stack gap={4} pl="md">
                {[
                  'Provide, operate, and maintain the Service;',
                  'Personalize your learning experience and generate study recommendations;',
                  'Process transactions and manage subscriptions;',
                  'Send administrative communications such as account confirmations and billing notices;',
                  'Monitor and analyze usage trends to improve the platform;',
                  'Detect and prevent fraudulent or unauthorized activity;',
                  'Comply with legal obligations.',
                ].map((item) => (
                  <Text key={item} c="var(--sat-text-muted)" lh={1.8}>
                    · {item}
                  </Text>
                ))}
              </Stack>
            </LegalSection>

            <LegalSection title="3. Sharing of Information">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We do not sell your personal information. We may share information with:
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Service Providers.</strong> Third-party vendors who assist in operating the
                Service (e.g., Stripe for payments, cloud hosting providers). These parties are
                contractually obligated to protect your data.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Tutors.</strong> If you are a student, your tutor can view your progress,
                assignment results, and study activity within the platform.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Legal Requirements.</strong> We may disclose information if required by law or
                to protect the rights, property, or safety of FinderAcademy, our users, or others.
              </Text>
            </LegalSection>

            <LegalSection title="4. Data Retention">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We retain your personal data for as long as your account is active or as needed to
                provide the Service. You may request deletion of your account and associated data by
                contacting us at privacy@finderacademy.org. We will respond within 30 days.
              </Text>
            </LegalSection>

            <LegalSection title="5. Cookies and Tracking">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We use authentication tokens stored in browser localStorage to keep you logged in. We
                do not use third-party advertising cookies. We may use minimal analytics to understand
                aggregate usage patterns.
              </Text>
            </LegalSection>

            <LegalSection title="6. Security">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We implement industry-standard security measures including HTTPS encryption, hashed
                passwords, and JWT-based authentication. No method of transmission over the Internet is
                100% secure; we cannot guarantee absolute security but commit to prompt disclosure of
                any breach as required by applicable law.
              </Text>
            </LegalSection>

            <LegalSection title="7. Children's Privacy">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                The Service is intended for users aged 13 and older. We do not knowingly collect
                personal information from children under 13. If you believe we have inadvertently
                collected such information, contact us immediately at privacy@finderacademy.org.
              </Text>
            </LegalSection>

            <LegalSection title="8. Your Rights">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                Depending on your jurisdiction, you may have the right to access, correct, or delete
                your personal data, object to or restrict processing, and request data portability.
                To exercise these rights, email privacy@finderacademy.org.
              </Text>
            </LegalSection>

            <LegalSection title="9. Changes to This Policy">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We may update this Privacy Policy from time to time. We will notify registered users
                via email or an in-app notice at least 14 days before material changes take effect.
                Continued use of the Service after the effective date constitutes acceptance of the
                updated policy.
              </Text>
            </LegalSection>

            <LegalSection title="10. Contact Us">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                FinderAcademy, Inc.<br />
                Email: privacy@finderacademy.org<br />
                Website: finderacademy.org
              </Text>
            </LegalSection>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
